import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { Controller } from '@/types';
import type { ApiMQTTServer, ApiLNSServer, CreateControllerRequest, UpdateControllerRequest } from '@/types/api';
import { MQTTServerFormDialog } from '@/components/servers/MQTTServerFormDialog';
import { LNSServerFormDialog } from '@/components/servers/LNSServerFormDialog';
import { cn } from '@/lib/utils';

// CAMBIO v0.2.0: Los controladores pertenecen a empresas, no a jardines
// CAMBIO v0.4.0: Modelos ahora incluyen versión de firmware (V2 o V4)
const controllerSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(100),
  model: z.enum(['UC511_V2', 'UC511_V4', 'UC512_V2', 'UC512_V4']),
  deviceEUI: z.string().min(16, 'DevEUI debe tener 16 caracteres').max(16),
  // companyId es opcional (superadmin puede asignar, company_admin no puede crear)
  companyId: z.string().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  communicationType: z.enum(['mqtt', 'chirpstack']),
  serverId: z.string().min(1, 'Selecciona un servidor'),
  subscriptionTopic: z.string().optional(),
  publishTopic: z.string().optional(),
  appKey: z.string().optional(), // AppKey para ChirpStack (32 hex chars, opcional)
}).superRefine((data, ctx) => {
  if (data.communicationType === 'mqtt') {
    if (!data.subscriptionTopic || data.subscriptionTopic.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El tópico de suscripción es requerido para MQTT',
        path: ['subscriptionTopic'],
      });
    }
    if (!data.publishTopic || data.publishTopic.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El tópico de publicación es requerido para MQTT',
        path: ['publishTopic'],
      });
    }
  }
  // Validar appKey si se proporciona (debe ser 32 caracteres hex)
  if (data.communicationType === 'chirpstack' && data.appKey && data.appKey.length > 0) {
    if (!/^[A-Fa-f0-9]{32}$/.test(data.appKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'AppKey debe ser 32 caracteres hexadecimales',
        path: ['appKey'],
      });
    }
  }
});

type ControllerFormData = z.infer<typeof controllerSchema>;

interface ControllerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controller?: Controller | null;
  // CAMBIO v0.2.0: empresas en lugar de jardines
  companies?: { id: string; name: string }[];
  mqttServers?: ApiMQTTServer[];
  lnsServers?: ApiLNSServer[];
  onSubmit: (data: CreateControllerRequest | UpdateControllerRequest) => void;
  isLoading?: boolean;
  // Rol del usuario actual (para mostrar/ocultar campos)
  userRole?: 'superadmin' | 'company_admin';
}

// Custom native select component for better compatibility
interface NativeSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
}

function NativeSelect({ value, onChange, placeholder, options, className, disabled }: NativeSelectProps) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer",
          !value && "text-muted-foreground",
          className
        )}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
    </div>
  );
}

export function ControllerFormDialog({
  open,
  onOpenChange,
  controller,
  companies = [],
  mqttServers = [],
  lnsServers = [],
  onSubmit,
  isLoading = false,
  userRole = 'company_admin',
}: ControllerFormDialogProps) {
  const isEditing = !!controller;
  const isSuperadmin = userRole === 'superadmin';
  const [mqttDialogOpen, setMqttDialogOpen] = useState(false);
  const [lnsDialogOpen, setLnsDialogOpen] = useState(false);

  // Determinar el serverId inicial basado en el tipo de comunicación
  const getInitialServerId = () => {
    if (!controller) return '';
    if (controller.communicationType === 'mqtt') {
      return controller.mqttConfig?.serverId || '';
    }
    return controller.lnsConfig?.serverId || '';
  };

  const form = useForm<ControllerFormData>({
    resolver: zodResolver(controllerSchema),
    defaultValues: {
      name: '',
      model: 'UC511_V2',
      deviceEUI: '',
      companyId: '',
      lat: undefined,
      lng: undefined,
      communicationType: 'mqtt',
      serverId: '',
      subscriptionTopic: '',
      publishTopic: '',
      appKey: '',
    },
  });

  const communicationType = form.watch('communicationType');

  // Reset form when dialog opens with controller data
  useEffect(() => {
    if (open) {
      if (controller) {
        console.log('[ControllerFormDialog] Loading controller data:', {
          name: controller.name,
          model: controller.model,
          deviceEUI: controller.deviceEUI,
          companyId: controller.companyId,
          communicationType: controller.communicationType,
        });
        form.reset({
          name: controller.name || '',
          model: controller.model || 'UC511_V2',
          deviceEUI: controller.deviceEUI || '',
          companyId: controller.companyId || '',
          lat: controller.coordinates?.lat,
          lng: controller.coordinates?.lng,
          communicationType: controller.communicationType || 'mqtt',
          serverId: getInitialServerId(),
          subscriptionTopic: controller.mqttConfig?.subscriptionTopic || '',
          publishTopic: controller.mqttConfig?.publishTopic || '',
          appKey: (controller as any).appKey || '',
        });
      } else {
        form.reset({
          name: '',
          model: 'UC511_V2',
          deviceEUI: '',
          companyId: '',
          lat: undefined,
          lng: undefined,
          communicationType: 'mqtt',
          serverId: '',
          subscriptionTopic: '',
          publishTopic: '',
          appKey: '',
        });
      }
    }
  }, [open, controller, form]);

  // Reset server and topics when communication type changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'communicationType') {
        // Limpiar servidor al cambiar tipo
        form.setValue('serverId', '');
        // Limpiar campos MQTT si se cambia a ChirpStack
        if (value.communicationType !== 'mqtt') {
          form.setValue('subscriptionTopic', '');
          form.setValue('publishTopic', '');
        }
        // Limpiar appKey si se cambia a MQTT
        if (value.communicationType !== 'chirpstack') {
          form.setValue('appKey', '');
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const handleSubmit = (data: ControllerFormData) => {
    // CAMBIO v0.2.0: Usar companyId en lugar de gardenId
    // Formato según DISCREPANCIAS_API.md
    const apiData: any = {
      name: data.name,
      deviceEUI: data.deviceEUI.toUpperCase(),
      communicationType: data.communicationType,
    };

    // Incluir model solo si estamos creando (no actualizando) o si el modelo cambió
    if (!controller) {
      // Crear: siempre incluir model
      apiData.model = data.model;
    } else if (data.model !== controller.model) {
      // Actualizar: incluir model solo si cambió
      apiData.model = data.model;
    }

    // companyId solo si es superadmin y se ha seleccionado
    if (data.companyId) {
      apiData.companyId = data.companyId;
    }

    // Coordenadas (campos directos lat/lng)
    if (data.lat !== undefined && data.lng !== undefined) {
      apiData.lat = data.lat;
      apiData.lng = data.lng;
    }

    // Configuración de comunicación
    if (!controller) {
      // Crear: usar objetos según ENDPOINT_TESTS
      if (data.communicationType === 'mqtt') {
        apiData.mqttConfig = {
          serverId: data.serverId,
          subscriptionTopic: data.subscriptionTopic,
          publishTopic: data.publishTopic,
        };
      } else {
        apiData.lnsConfig = {
          serverId: data.serverId,
        };
        if (data.appKey && data.appKey.trim().length > 0) {
          apiData.appKey = data.appKey.toUpperCase();
        }
      }
    } else {
      // Actualizar: usar campos directos según API_REFERENCE.md (PUT /controllers/:id)
      // IMPORTANTE: Siempre enviar communicationType y todos los campos de conectividad
      const previousType = controller.communicationType;
      const typeChanged = previousType !== data.communicationType;
      
      if (data.communicationType === 'mqtt') {
        // Para MQTT: enviar campos directos
        if (data.serverId) {
          apiData.mqttServerId = data.serverId;
        }
        // Enviar topics incluso si están vacíos para permitir limpiarlos
        apiData.mqttSubscriptionTopic = data.subscriptionTopic || '';
        apiData.mqttPublishTopic = data.publishTopic || '';
        
        // Si se cambió de ChirpStack a MQTT, limpiar campos LNS
        if (typeChanged && previousType === 'chirpstack') {
          apiData.lnsServerId = null; // Limpiar servidor LNS
          apiData.appKey = null; // Limpiar appKey
        }
      } else {
        // Para ChirpStack: usar lnsServerId directamente (no lnsConfig)
        if (data.serverId) {
          apiData.lnsServerId = data.serverId;
        }
        // Enviar appKey si está presente
        if (data.appKey && data.appKey.trim().length > 0) {
          apiData.appKey = data.appKey.toUpperCase();
        }
        
        // Si se cambió de MQTT a ChirpStack, limpiar campos MQTT
        if (typeChanged && previousType === 'mqtt') {
          apiData.mqttServerId = null; // Limpiar servidor MQTT
          apiData.mqttSubscriptionTopic = null; // Limpiar topics
          apiData.mqttPublishTopic = null;
        }
      }
    }

    console.log('[Controller Form] Submitting data:', JSON.stringify(apiData, null, 2));
    onSubmit(apiData);
    form.reset();
    onOpenChange(false);
  };

  const servers = communicationType === 'mqtt' ? mqttServers : lnsServers;

  // Debug: verificar que los servidores se están cargando
  useEffect(() => {
    if (open) {
      console.log('[ControllerFormDialog] Servers loaded:', {
        communicationType,
        mqttServersCount: mqttServers.length,
        lnsServersCount: lnsServers.length,
        currentServers: servers.length,
        servers: servers.map(s => ({ id: s.id, name: s.name })),
      });
    }
  }, [open, communicationType, mqttServers, lnsServers, servers]);

  const modelOptions = [
    { value: 'UC511_V2', label: 'UC511 V2 (Firmware v2.x)' },
    { value: 'UC511_V4', label: 'UC511 V4 (Firmware v4.x)' },
    { value: 'UC512_V2', label: 'UC512 V2 (Firmware v2.x)' },
    { value: 'UC512_V4', label: 'UC512 V4 (Firmware v4.x)' },
  ];

  const communicationOptions = [
    { value: 'mqtt', label: 'MQTT' },
    { value: 'chirpstack', label: 'ChirpStack / LNS' },
  ];

  // CAMBIO v0.2.0: Empresas en lugar de jardines
  const companyOptions = companies.map((company) => ({
    value: company.id,
    label: company.name,
  }));

  const serverOptions = servers.length > 0 
    ? servers.map((server) => ({
        value: server.id,
        label: server.name,
      }))
    : [{ value: '', label: `No hay servidores ${communicationType === 'mqtt' ? 'MQTT' : 'LNS'} disponibles` }];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {isEditing ? 'Editar Controladora' : 'Nueva Controladora'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Modifica los datos de la controladora.'
                : 'Añade una nueva controladora UC511 o UC512 al sistema.'}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input placeholder="Controladora Zona Norte" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="model"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Modelo *</FormLabel>
                      <FormControl>
                        <NativeSelect
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Seleccionar modelo"
                          options={modelOptions}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deviceEUI"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>DevEUI *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="A84041XXXXXXXXXX"
                          className="font-mono"
                          maxLength={16}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* CAMBIO v0.2.0: Empresa en lugar de jardín - solo visible para superadmin */}
              {isSuperadmin && (
                <FormField
                  control={form.control}
                  name="companyId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Empresa</FormLabel>
                      <FormControl>
                        <NativeSelect
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder="Seleccionar empresa (opcional)"
                          options={[{ value: '', label: 'Sin empresa asignada' }, ...companyOptions]}
                        />
                      </FormControl>
                      <FormDescription>
                        La controladora se asignará a esta empresa. La relación con jardines se establece mediante zonas.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Coordenadas (opcionales) */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="lat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Latitud</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="any"
                          placeholder="39.4699"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lng"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Longitud</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="any"
                          placeholder="-0.3763"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Sección de Comunicación */}
              <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
                <h4 className="font-medium text-sm">Configuración de Comunicación</h4>
                
                <FormField
                  control={form.control}
                  name="communicationType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Comunicación *</FormLabel>
                      <FormControl>
                        <NativeSelect
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Seleccionar tipo"
                          options={communicationOptions}
                        />
                      </FormControl>
                      <FormDescription>
                        {communicationType === 'mqtt' 
                          ? 'Comunicación directa mediante broker MQTT'
                          : 'Comunicación a través de servidor LoRaWAN (ChirpStack)'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="serverId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {communicationType === 'mqtt' ? 'Servidor MQTT *' : 'Servidor LNS *'}
                      </FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <NativeSelect
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={`Seleccionar servidor ${communicationType === 'mqtt' ? 'MQTT' : 'LNS'}`}
                            options={serverOptions}
                            className="flex-1"
                          />
                        </FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          title={`Añadir servidor ${communicationType === 'mqtt' ? 'MQTT' : 'LNS'}`}
                          onClick={() => {
                            if (communicationType === 'mqtt') {
                              setMqttDialogOpen(true);
                            } else {
                              setLnsDialogOpen(true);
                            }
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Campos MQTT */}
                {communicationType === 'mqtt' && (
                  <>
                    <FormField
                      control={form.control}
                      name="subscriptionTopic"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tópico de Suscripción (RX) *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="irrigo/controllers/{deviceEUI}/rx"
                              className="font-mono text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Tópico para recibir mensajes del dispositivo
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="publishTopic"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tópico de Publicación (TX) *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="irrigo/controllers/{deviceEUI}/tx"
                              className="font-mono text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Tópico para enviar comandos al dispositivo
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {/* Campo AppKey para ChirpStack */}
                {communicationType === 'chirpstack' && (
                  <FormField
                    control={form.control}
                    name="appKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>AppKey (opcional)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="AABBCCDDEEFF00112233445566778899"
                            className="font-mono text-sm uppercase"
                            maxLength={32}
                            {...field}
                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          />
                        </FormControl>
                        <FormDescription>
                          32 caracteres hexadecimales. Si se deja vacío, se generará automáticamente.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="gradient-primary text-white" disabled={isLoading}>
                  {isLoading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear Controladora'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* MQTT Server Dialog */}
      <MQTTServerFormDialog
        open={mqttDialogOpen}
        onOpenChange={setMqttDialogOpen}
        onSubmit={(data) => {
          console.log('New MQTT server:', data);
          setMqttDialogOpen(false);
        }}
      />

      {/* LNS Server Dialog */}
      <LNSServerFormDialog
        open={lnsDialogOpen}
        onOpenChange={setLnsDialogOpen}
        onSubmit={(data) => {
          console.log('New LNS server:', data);
          setLnsDialogOpen(false);
        }}
      />
    </>
  );
}
